import { safeReturnTo } from '@server/return-to';
import { SIGN_IN_PAGES, type SignInPage, signInPagePath } from '@server/shared-sign-in';
import { SIGNUP_REGIONS, type SignupRegion } from '@server/signup-answers';
import { z } from 'zod';

import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';

import type { NativeSignInConfig } from '../types';
import { checkFederatedSignIn, LookoutSessionError, startFromSharedSession } from './lookout-session';
import { startFederatedSession } from './rudderstack-auth-client';
import { WEB_APP_ENTRY } from './web-app-entry';

/**
 * Google and SSO with the shared session (ACT2-879). As in the web app, the page runs the Cognito Hosted UI with PKCE,
 * and keeps the state and the code verifier in this tab. So a callback that another browser or tab started fails.
 */
export type HostedUiConfig = NonNullable<NativeSignInConfig['hostedUi']>;

/** Where the Hosted UI returns. Registered on the web app client, next to Lookout's server callback. */
export const HOSTED_UI_CALLBACK_PATH = '/login/callback';

const STORAGE_KEY = 'lookout.hostedUiSignIn';
/** Cognito identity provider names: 'Google', or an SSO organization id. The same rule as Lookout's server flow. */
const IDENTITY_PROVIDER_PATTERN = /^[A-Za-z0-9_.-]{1,128}$/;

const SavedFlowSchema = z
  .object({
    state: z.string().min(1),
    verifier: z.string().min(1),
    returnTo: z.string().nullable(),
    invite: z.boolean(),
    region: z.enum(SIGNUP_REGIONS).optional(),
    page: z.enum(SIGN_IN_PAGES).optional(),
  })
  .transform(({ page, ...flow }) => {
    // A flow saved before `page` existed: a Google sign-up has a region and started on `/signup`.
    const start: SignInPage = page ?? (flow.region ? 'signup' : 'login');
    return { ...flow, page: start };
  });

/** The sign-in this tab started. `region` makes a Google start a sign-up; an invitee picks none. Errors go to `page`. */
export type HostedUiFlow = Omit<z.infer<typeof SavedFlowSchema>, 'state' | 'verifier'> & { verifier: string };

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function randomValue(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

export function hostedUiCallbackUrl(): string {
  return `${window.location.origin}${HOSTED_UI_CALLBACK_PATH}`;
}

/** Opens the Hosted UI for Google or an SSO identity provider. The analytics start event goes from the page. */
export async function startHostedUiSignIn(
  hostedUi: HostedUiConfig,
  target: { provider: 'google' } | { provider: 'sso'; idp: string },
  options: { returnTo: string | null; invite: boolean; fromWebApp?: boolean; region?: SignupRegion; page?: SignInPage },
): Promise<void> {
  const identityProvider = target.provider === 'google' ? 'Google' : target.idp;
  if (!IDENTITY_PROVIDER_PATTERN.test(identityProvider) || /^cognito$/i.test(identityProvider)) {
    throw new Error('Sign-in with Google or SSO failed. Please try again.');
  }
  const state = randomValue();
  const verifier = randomValue();
  const saved: z.infer<typeof SavedFlowSchema> = {
    state,
    verifier,
    returnTo: options.returnTo,
    invite: options.invite,
    ...(options.region && !options.invite ? { region: options.region } : {}),
    page: options.page ?? 'login',
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
  trackClientEvent(ANALYTICS_EVENTS.userSignInStarted, {
    provider: 'rudderstack',
    method: target.provider,
    hasReturnTo: options.returnTo !== null,
    ...(options.fromWebApp ? { entryPoint: WEB_APP_ENTRY } : {}),
  });
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: hostedUi.clientId,
    redirect_uri: hostedUiCallbackUrl(),
    scope: 'openid profile email',
    state,
    code_challenge: await codeChallenge(verifier),
    code_challenge_method: 'S256',
    identity_provider: identityProvider,
  });
  window.location.assign(`${hostedUi.url}/oauth2/authorize?${params.toString()}`);
}

/** The flow this tab started for `state`, or null. It is removed either way, so a callback works once. */
export function takeHostedUiFlow(state: string | null): HostedUiFlow | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  if (!raw || !state) return null;
  let parsed;
  try {
    parsed = SavedFlowSchema.safeParse(JSON.parse(raw));
  } catch {
    return null;
  }
  if (!parsed.success || parsed.data.state !== state) return null;
  const { state: _state, ...flow } = parsed.data;
  return flow;
}

const TokenReplySchema = z.object({ id_token: z.string().min(1), refresh_token: z.string().min(1) });

/** Exchanges the Hosted UI code. The web app client is public, so the PKCE verifier replaces a client secret. */
export async function exchangeHostedUiCode(
  hostedUi: HostedUiConfig,
  input: { code: string; verifier: string },
): Promise<{ idToken: string; refreshToken: string }> {
  const res = await fetch(`${hostedUi.url}/oauth2/token`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: hostedUi.clientId,
      code: input.code,
      code_verifier: input.verifier,
      redirect_uri: hostedUiCallbackUrl(),
    }).toString(),
  });
  const parsed = TokenReplySchema.safeParse(await res.json().catch(() => null));
  if (!res.ok || !parsed.success) throw new Error(`Cognito token exchange failed: HTTP ${res.status}`);
  return { idToken: parsed.data.id_token, refreshToken: parsed.data.refresh_token };
}

/** The page that started the sign-in, with an error code. The same rule as Lookout's server. */
function errorPath(code: string, flow: HostedUiFlow | null): string {
  return signInPagePath(
    { page: flow?.page ?? 'login', invite: flow?.invite ?? false, returnTo: safeReturnTo(flow?.returnTo) },
    code,
  );
}

/**
 * Finishes a Google or SSO sign-in on `/login/callback`, and returns the page to open. The steps: exchange the code,
 * let Lookout check the sign-in and call config-backend's /authSignin, start the shared session, then start Lookout's
 * session from a one-time code of the shared session.
 */
export async function finishHostedUiSignIn(search: string, config: NativeSignInConfig | null): Promise<string> {
  const params = new URLSearchParams(search);
  const flow = takeHostedUiFlow(params.get('state'));
  const code = params.get('code');
  const hostedUi = config?.sharedSession ? config.hostedUi : null;
  if (!config || !hostedUi || !flow || !code || params.has('error')) {
    return errorPath('federated_signin_failed', flow);
  }

  let tokens;
  try {
    tokens = await exchangeHostedUiCode(hostedUi, { code, verifier: flow.verifier });
  } catch {
    return errorPath('federated_signin_failed', flow);
  }
  const returnTo = safeReturnTo(flow.returnTo);
  let checked;
  try {
    checked = await checkFederatedSignIn({
      idToken: tokens.idToken,
      invite: flow.invite,
      region: flow.region,
      returnTo,
      page: flow.page,
    });
  } catch {
    return errorPath('login_failed', flow);
  }
  if (checked.kind === 'refused') return checked.redirectTo;

  try {
    await startFederatedSession(config.apiBaseUrl, tokens);
  } catch {
    return errorPath('federated_signin_failed', flow);
  }
  try {
    const started = await startFromSharedSession(config.apiBaseUrl, { returnTo, method: checked.method });
    // config-backend started the session, but the browser kept no cookie.
    return started.kind === 'signed_in' ? started.redirectTo : errorPath('login_failed', flow);
  } catch (error) {
    return errorPath(error instanceof LookoutSessionError ? error.code : 'login_failed', flow);
  }
}
